"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Send, PhoneCall, Mail } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPES = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"] as const;
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"] as const;
const BUDGET_RANGES = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"] as const;
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [projectType, setProjectType] = useState("");
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    setZip(val);
    if (zipError && /^\d{5}$/.test(val)) {
      setZipError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    let hasError = false;
    if (!/^\d{5}$/.test(zip)) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      hasError = true;
    } else {
      setZipError("");
    }
    if (!consent) {
      setConsentError("Please agree to the contact terms to proceed.");
      hasError = true;
    } else {
      setConsentError("");
    }
    if (hasError) return;
    setIsSubmitting(true);
    try {
      const payload = new URLSearchParams({
        name,
        phone,
        email,
        project_type: projectType,
        address: zip,
        zip,
        details: message,
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "remodeling-cost-guide-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative w-full bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl border border-border/80 bg-card/85 p-6 shadow-xl backdrop-blur-md sm:p-10 md:p-12">
          {/* Header */}
          <div className="mx-auto mb-8 max-w-2xl text-center md:mb-10">
            <Badge variant="outline" className="mb-3 border-primary/40 bg-accent/60 text-accent-foreground font-mono text-xs uppercase tracking-wider">
              Project Consultation
            </Badge>
            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Request an Estimate
            </h2>
            <p className="mt-3 text-sm text-foreground/80 sm:text-base">
              Share details about your upcoming project and we will follow up to discuss your project with a thoughtful, personalized estimate.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {isSubmitted ? <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="rounded-xl border border-border bg-background/60 p-8 text-center sm:p-12">
                <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary/20 text-primary">
                  <CheckCircle2 className="size-8 text-primary" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-foreground">
                  Thank You for Reaching Out
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground/80 sm:text-base">
                  Your estimate request has been safely received. A dedicated remodeling specialist from Brookhaven Elevation Renovations will contact you shortly to review your project scope.
                </p>

                <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <a href="tel:4708479076" className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-2xs hover:bg-accent transition-colors">
                    <PhoneCall className="size-4 text-primary" />
                    <span>(470) 847-9076</span>
                  </a>
                  <a href="mailto:serviceleadalerts@gmail.com" className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-2xs hover:bg-accent transition-colors">
                    <Mail className="size-4 text-primary" />
                    <span>serviceleadalerts@gmail.com</span>
                  </a>
                </div>
              </motion.div> : <form key="form" onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                    <AlertCircle className="size-5" />
                    <AlertDescription className="text-foreground text-sm font-medium">
                      {errorMessage}
                    </AlertDescription>
                  </Alert>}

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* Full Name */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-full-name" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="form-full-name" type="text" required placeholder="Jane Doe" value={name} onChange={e => setName(e.target.value)} className="border-border/90 bg-background/80 h-10 px-3.5 focus-visible:ring-primary" />
                  </div>

                  {/* Phone Number */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-phone" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="form-phone" type="tel" required placeholder="(404) 555-0123" value={phone} onChange={e => setPhone(e.target.value)} className="border-border/90 bg-background/80 h-10 px-3.5 focus-visible:ring-primary" />
                  </div>

                  {/* Email Address */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-email" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="form-email" type="email" required placeholder="jane@example.com" value={email} onChange={e => setEmail(e.target.value)} className="border-border/90 bg-background/80 h-10 px-3.5 focus-visible:ring-primary" />
                  </div>

                  {/* ZIP Code */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-zip" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="form-zip" type="text" required maxLength={5} placeholder="30319" value={zip} onChange={handleZipChange} aria-invalid={!!zipError} className="border-border/90 bg-background/80 h-10 px-3.5 focus-visible:ring-primary" />
                    {zipError && <p className="text-xs font-medium text-destructive mt-1">
                        {zipError}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  {/* Project Type */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-project-type" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <select id="form-project-type" required value={projectType} onChange={e => setProjectType(e.target.value)} className="border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-md border bg-background/80 px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                        <option value="" disabled>
                          Select project type
                        </option>
                        {PROJECT_TYPES.map((type, i) => <option key={type} data-index={i} value={type} className="text-foreground bg-card">
                            {type}
                          </option>)}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="size-4 text-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Preferred Timing */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-timing" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      Preferred Timing
                    </Label>
                    <div className="relative">
                      <select id="form-timing" value={timing} onChange={e => setTiming(e.target.value)} className="border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-md border bg-background/80 px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                        <option value="">Select timeframe (optional)</option>
                        {TIMING_OPTIONS.map((item, i) => <option key={item} data-index={i} value={item} className="text-foreground bg-card">
                            {item}
                          </option>)}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="size-4 text-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Budget Range */}
                  <div className="space-y-1.5">
                    <Label htmlFor="form-budget" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      Budget Range
                    </Label>
                    <div className="relative">
                      <select id="form-budget" value={budget} onChange={e => setBudget(e.target.value)} className="border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-md border bg-background/80 px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                        <option value="">Select budget (optional)</option>
                        {BUDGET_RANGES.map((range, i) => <option key={range} data-index={i} value={range} className="text-foreground bg-card">
                            {range}
                          </option>)}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="size-4 text-foreground/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Details / Message */}
                <div className="space-y-1.5">
                  <Label htmlFor="form-message" className="text-xs font-semibold uppercase tracking-wider text-foreground">
                    Project Details / Message
                  </Label>
                  <Textarea id="form-message" rows={4} placeholder="Tell us about your home, desired features, goals, or any specific ideas you have..." value={message} onChange={e => setMessage(e.target.value)} className="border-border/90 bg-background/80 min-h-24 px-3.5 py-2.5 focus-visible:ring-primary" />
                </div>

                {/* Consent Checkbox */}
                <div className="pt-2">
                  <div className="flex items-start space-x-3">
                    <Checkbox id="form-consent" checked={consent} onCheckedChange={checked => {
                  const val = checked === true;
                  setConsent(val);
                  if (val) setConsentError("");
                }} className="mt-0.5 border-border" />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="form-consent" className="text-xs font-normal text-foreground/90 leading-relaxed cursor-pointer select-none">
                        I agree to be contacted about my project by phone, text, or email.
                      </Label>
                      {consentError && <p className="text-xs font-medium text-destructive">
                          {consentError}
                        </p>}
                    </div>
                  </div>
                </div>

                {/* Action button */}
                <div className="pt-4 flex flex-col items-center sm:items-start">
                  <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold py-3.5 px-8 rounded-lg shadow-sm hover:translate-y-[-2px] transition-transform duration-200">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin" />
                        <span>Sending Request...</span>
                      </> : <>
                        <span>Submit Estimate Request</span>
                        <Send className="size-4 ml-1.5" />
                      </>}
                  </Button>
                </div>
              </form>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}